import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { wp } from "../api/WpApi";
import "../../styles/wpStyles.scss";
import { ItemListSchema } from "../seo/ItemListSchema";
import Breadcrumbs from "../helpers/Breadcrumbs";
import PageLoading from "./PageLoading";
import data from "./_setup.json";
import MetaTags from "../seo/MetaTags";
import { Helmet } from "react-helmet";
import DEFAULT_THUMBNAIL from "../../resources/not-found.png";
import Pagination from "./Pagination";
import { parseImgurThumbnail } from "../helpers/ImgurImageParser";

const Categories = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const { page } = useParams();
  const navigate = useNavigate();
  const initialPage = page ? parseInt((page + "").replace(/[^0-9]/g, ""), 10) || 1 : 1;
  const [currentpage, setCurrentPage] = useState(initialPage);
  const [totalpages, setTotalPages] = useState(null);
  const [perPage] = useState(12);

  const breadcrumbsItems = [
    { name: "Home", url: "/" },
    { name: "All Recipes", url: `/${data.url.slug_posts}/` },
    { name: "Categories", url: `/${process.env.REACT_APP_PUBLIC_URL || ""}/${data.url.slug_posts}/${data.url.slug_categories}/` },
  ];



  const getCategories = useCallback(
    (page) => {
      setIsLoading(true);
      wp.categories()
        .perPage(perPage)
        .page(page)
        .then((cats) => {
          // For each category fetch one post to extract the thumbnail from yoast_head_json
          if (!Array.isArray(cats)) {
            setCategories([]);
            setTotalPages(1);
            setIsLoading(false);
            return;
          }

          const promises = cats.map((cat) =>
            wp
              .posts()
              .categories(cat.id)
              .perPage(1)
              .then((posts) => {
                const thumb =
                  posts && posts[0] && posts[0].yoast_head_json && posts[0].yoast_head_json.og_image
                    ? parseImgurThumbnail(posts[0].yoast_head_json.og_image[0].url)
                    : DEFAULT_THUMBNAIL;
                return { ...cat, thumbnail: thumb };
              })
              .catch(() => ({ ...cat, thumbnail: DEFAULT_THUMBNAIL }))
          );

          Promise.all(promises)
            .then((catsWithThumbs) => {
              setCategories(catsWithThumbs);
              setTotalPages(cats._paging && cats._paging.totalPages ? cats._paging.totalPages : 1);
              setIsLoading(false);
            })
            .catch((err) => {
              setError(err);
              setIsLoading(false);
            });
        })
        .catch((error) => {
          setError(error);
          setIsLoading(false);
        });
    },
    [perPage]
  );

  useEffect(() => {
    getCategories(currentpage);
  }, [currentpage, getCategories]);

  // Keep currentpage in sync with URL param (normalize digits only)
  useEffect(() => {
    const p = page ? parseInt((page + "").replace(/[^0-9]/g, ""), 10) || 1 : 1;
    if (p !== currentpage) setCurrentPage(p);
  }, [page]);

  var itemListArray = [];
  var listCount = 1;

  if (error) {
    return (
      <div className="content">
        <Helmet>
          <meta name="robots" content="noindex"></meta>
        </Helmet>
        Error: {error.message}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container">
        <PageLoading />
      </div>
    );
  }

  if (Array.isArray(categories) && categories.length > 0) {
    return (
      <div className="content container">
        <br />
        <MetaTags name="description" content="All categories" />
        <Helmet>
          <title>Categories | Andrew's Cookin'</title>
          <link
            rel="canonical"
            href={`${process.env.REACT_APP_PUBLIC_URL}/${data.url.slug_posts}/${data.url.slug_categories}/`}
          />
        </Helmet>

        <div className="row">
          <div className="grid grid-cols-1 md:grid-cols-3 justify-content-between align-items-center">
            <div className="md:col-start-2 md:col-end-2">
              <h1 className="fw-bold">Pick a Category</h1>
              <small className="fw-light">Here you will find all the different recipe categories.</small>
            </div>
          </div>
        </div>

        <Breadcrumbs items={breadcrumbsItems} />
        <hr />

        <div className="row">
          <div className="list-group col">
            {categories.map((categorie) => {
              itemListArray.push({
                "@type": "ListItem",
                position: listCount++,
                url: categorie.link,
              });

              return (
                <Link
                  key={categorie.id}
                  to={categorie.slug + "/"}
                  className="shadow-sm list-group-item list-group-item-action transition ease-in-out delay-50 duration-300 grid grid-rows-6"
                >
                  <div className="row-span-5 md:grid gap-6 md:justify-items-between justify-items-center">
                  <h2 className="fw-bold " dangerouslySetInnerHTML={{ __html: categorie.name }} />
                    
                    <img
                    loading="lazy"
                    className="category-thumbnail md:row-span-3"
                      src={categorie.thumbnail || DEFAULT_THUMBNAIL}
                      alt={categorie.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = DEFAULT_THUMBNAIL;
                      }}
                    />
                      
                    <p className="md:order-3 md:row-span-1 text-center" dangerouslySetInnerHTML={{ __html: categorie.description }} />
                  </div>
                    <small className="row-span-1">Total Posts: { categorie.count}</small>

                </Link>
              );
            })}

            <Helmet>
              <script type="application/ld+json">{JSON.stringify(ItemListSchema(itemListArray))}</script>
            </Helmet>
          </div>
        </div>

        {currentpage <= totalpages && (
          <div className="row">
            <div className="col-md">
              <div className="row">
                <div className="col-md d-flex justify-content-center align-items-center my-5">
                            <Pagination
                              currentPage={currentpage}
                              totalPages={totalpages}
                              onPageChange={(pageNum) => {
                                setCurrentPage(pageNum);
                                const base = `/${data.url.slug_posts}/${data.url.slug_categories}`;
                                navigate(pageNum === 1 ? `${base}/` : `${base}/page-${pageNum}`);
                              }}
                            />
                </div>
              </div>
            </div>
          </div>
        )}

        <hr />
      </div>
    );
  }

  return <div className="content">Nothing to View!</div>;
};

export default Categories;
